import type { SEOConfig } from './types';
import { BASE_URL, DEFAULT_OG_IMAGE } from './types';

export type { SEOConfig } from './types';
export { BASE_URL, DEFAULT_OG_IMAGE } from './types';

export const defaultSEO: SEOConfig = {
  title: 'Developmental Support for Neurodivergent Children | TinyWins',
  description:
    'TinyWins.ai helps parents and therapists support neurodivergent children with personalized milestones, sensory-aware strategies, and progress tracking.',
  keywords: [
    'TinyWins.ai',
    'neurodivergent children support',
    'autism support app',
    'developmental milestones tracker',
    'parent therapist collaboration',
  ],
  url: BASE_URL,
  image: DEFAULT_OG_IMAGE,
  robots: 'index,follow',
  type: 'website',
};

const page = (
  path: string,
  partial: Omit<SEOConfig, 'url' | 'image'> & { image?: string; robots?: string },
): [string, SEOConfig] => [
  path,
  {
    ...defaultSEO,
    ...partial,
    image: partial.image ?? DEFAULT_OG_IMAGE,
    url: path === '/' ? BASE_URL : `${BASE_URL}${path}`,
  },
];

const publicPageSEO: Record<string, SEOConfig> = Object.fromEntries([
  page('/', {
    title: 'Developmental Support for Neurodivergent Children | TinyWins',
    description:
      'TinyWins.ai helps parents and therapists support neurodivergent children with personalized milestones, sensory-aware strategies, and progress tracking.',
    keywords: [
      'developmental support for neurodivergent children',
      'developmental milestone tracker',
      'autism goal tracking app',
      'sensory-aware strategies',
    ],
  }),
  page('/parents', {
    title: 'Developmental Support App for Parents | TinyWins',
    description:
      'Track developmental goals, use sensory-aware strategies, explore IEP-friendly tools, and support your neurodivergent child at home with TinyWins.',
    keywords: [
      'developmental support app for parents',
      'developmental milestone tracker',
      'autism goal-tracking app',
      'neurodivergent child-development tools',
    ],
  }),
  page('/therapists', {
    title: 'Therapist Marketplace & Practice Tools | TinyWins',
    description:
      'Join the TinyWins therapist marketplace, create a professional profile, and use goal-tracking tools to collaborate with families — without guaranteed client volume.',
    keywords: ['therapist marketplace', 'therapy practice management', 'neurodivergent therapy tools'],
  }),
  page('/schools-and-counties', {
    title: 'Schools & Counties Developmental Support | TinyWins',
    description:
      'Give families goal tracking and educational resources with organization-level roles, reporting options, and privacy-minded pilots for schools and counties.',
    keywords: ['school developmental support', 'county early intervention tools', 'organization reporting'],
  }),
  page('/how-it-works', {
    title: 'How TinyWins Works | Developmental Goal Tracking',
    description:
      'Create an account, add a child profile, select goals, receive personalized strategies, and track progress over time with TinyWins.',
    keywords: ['how TinyWins works', 'personalized milestone tracking', 'child progress steps'],
  }),
  page('/resources', {
    title: 'Parent & Educator Resources | TinyWins',
    description:
      'Educational resources on IEP goals, communication, sensory support, behavior and regulation, and visual schedules for everyday routines.',
    keywords: ['child development resources', 'IEP resources', 'sensory support resources'],
  }),
  page('/about', {
    title: 'About TinyWins | Our Story',
    description:
      'TinyWins was born from one family\'s journey — Vikrant, Anjali, and Arnav — to help parents see small developmental steps, from feeding therapy to everyday tiny wins.',
    keywords: ['about TinyWins', 'TinyWins story', 'feeding therapy', 'neurodivergent support mission'],
  }),
  page('/contact', {
    title: 'Contact TinyWins',
    description: 'Contact TinyWins.ai for product questions, partnerships, and support.',
    keywords: ['contact TinyWins', 'TinyWins help'],
  }),
  page('/privacy-policy', {
    title: 'Privacy Policy | TinyWins',
    description: 'Read how TinyWins.ai protects privacy and handles data for families, therapists, and organizations.',
    keywords: ['TinyWins privacy policy', 'child data protection'],
  }),
  page('/terms', {
    title: 'Terms of Service | TinyWins',
    description: 'Review the Terms of Service for using TinyWins.ai.',
    keywords: ['TinyWins terms', 'terms of service'],
  }),
  page('/developmental-milestone-tracker', {
    title: 'Developmental Milestone Tracker | TinyWins',
    description:
      'Track meaningful developmental goals and everyday progress in plain language — educational support, not a diagnostic tool.',
    keywords: ['developmental milestone tracker', 'goal tracking for kids', 'neurodivergent progress'],
  }),
  page('/iep-goal-coach', {
    title: 'IEP Goal Coach for Parents | TinyWins',
    description:
      'Organize, understand, and track IEP-related goals with educational coaching tools. TinyWins does not replace teachers, clinicians, or legal advice.',
    keywords: ['IEP goal coach', 'IEP goals for parents', 'special education goal tracking'],
  }),
  page('/social-stories', {
    title: 'Social Stories for Everyday Routines | TinyWins',
    description:
      'Use social stories for school routines, dentist visits, transitions, community activities, and daily living — practical support for families.',
    keywords: ['social stories', 'visual social narratives', 'transition social stories'],
  }),
  page('/sensory-strategies', {
    title: 'Sensory Strategies for Home Routines | TinyWins',
    description:
      'Explore sensory-aware strategies for everyday home and community routines. Educational guidance — not treatment claims.',
    keywords: ['sensory strategies', 'sensory-aware home routines', 'regulation strategies'],
  }),
  page('/developmental-goals/communication', {
    title: 'Communication Developmental Goals | TinyWins',
    description: 'Educational goals and strategies that support communication growth for neurodivergent children.',
    keywords: ['communication goals', 'speech language goals', 'expressive language support'],
  }),
  page('/developmental-goals/social-emotional', {
    title: 'Social-Emotional Developmental Goals | TinyWins',
    description: 'Track social-emotional goals and everyday strategies that support connection and regulation.',
    keywords: ['social emotional goals', 'emotional regulation goals'],
  }),
  page('/developmental-goals/daily-living', {
    title: 'Daily Living Developmental Goals | TinyWins',
    description: 'Support daily living and self-help goals with practical, family-friendly tracking.',
    keywords: ['daily living goals', 'self help skills', 'ADL goals'],
  }),
  page('/developmental-goals/cognitive', {
    title: 'Cognitive Developmental Goals | TinyWins',
    description: 'Educational cognitive and learning goals with strategies for everyday practice.',
    keywords: ['cognitive goals', 'learning goals for kids'],
  }),
  page('/developmental-goals/motor-skills', {
    title: 'Motor Skills Developmental Goals | TinyWins',
    description: 'Fine and gross motor goal ideas with sensory-aware practice for home and school routines.',
    keywords: ['motor skills goals', 'fine motor', 'gross motor'],
  }),
  page('/resources/iep', {
    title: 'IEP Resources for Families | TinyWins',
    description: 'Educational IEP resources to help families organize goals and collaborate with school teams.',
    keywords: ['IEP resources', 'IEP parent guide'],
  }),
  page('/resources/communication', {
    title: 'Communication Resources | TinyWins',
    description: 'Communication tools and educational guides for everyday language and interaction support.',
    keywords: ['communication resources', 'AAC parent resources'],
  }),
  page('/resources/sensory', {
    title: 'Sensory Resources | TinyWins',
    description: 'Sensory support resources for home routines and regulation — educational, not clinical treatment.',
    keywords: ['sensory resources', 'sensory activities'],
  }),
  page('/resources/behavior-and-regulation', {
    title: 'Behavior & Regulation Resources | TinyWins',
    description: 'Practical regulation and behavior-support resources for families and educators.',
    keywords: ['behavior regulation resources', 'emotional regulation'],
  }),
  page('/resources/visual-schedules', {
    title: 'Visual Schedules Resources | TinyWins',
    description: 'Visual schedule ideas that support transitions and predictable daily routines.',
    keywords: ['visual schedules', 'visual supports'],
  }),
  page('/team', {
    title: 'Team | TinyWins',
    description: 'Meet the TinyWins team building practical tools for families and therapists.',
    keywords: ['TinyWins team'],
  }),
  page('/support', {
    title: 'Support | TinyWins',
    description: 'Get help using TinyWins — platform guidance and account support.',
    keywords: ['TinyWins support'],
  }),
  page('/therapist-marketplace', {
    title: 'Therapist Marketplace | TinyWins',
    description:
      'Discover therapy professionals who support neurodivergent children. Marketplace participation does not guarantee clients.',
    keywords: ['therapist marketplace', 'find child therapist'],
  }),
  page('/book-meeting', {
    title: 'Book a Meeting | TinyWins',
    description: 'Book time with TinyWins to learn about parent tools, therapist plans, or organization pilots.',
    keywords: ['book TinyWins meeting'],
  }),
  page('/internships', {
    title: 'Internships | TinyWins',
    description: 'Explore internship opportunities with TinyWins.ai.',
    keywords: ['TinyWins internships'],
  }),
  page('/org-admin', {
    title: 'Organization Admin | TinyWins',
    description: 'Organization landing for TinyWins.',
    keywords: ['TinyWins organization'],
    robots: 'noindex,nofollow',
  }),
  page('/org-referral', {
    title: 'Organization Referral | TinyWins',
    description: 'Organization referral landing.',
    keywords: ['referral'],
    robots: 'noindex,nofollow',
  }),
  page('/parent-referral', {
    title: 'Parent Referral | TinyWins',
    description: 'Parent referral landing.',
    keywords: ['referral'],
    robots: 'noindex,nofollow',
  }),
  page('/blog', {
    title: 'Blog | TinyWins',
    description: 'Guides on child development and neurodiversity.',
    keywords: ['TinyWins blog'],
    robots: 'noindex,nofollow',
  }),
  page('/faq', {
    title: 'FAQ | TinyWins',
    description: 'Answers about TinyWins milestones, sensory strategies, and collaboration.',
    keywords: ['TinyWins FAQ'],
  }),
]);

const privatePathPrefixes = [
  '/admin',
  '/admin-dashboard',
  '/org-admin-dashboard',
  '/kid-profile',
  '/profile',
  '/profile-setup',
  '/kid-profile-form',
  '/parent-child-setup',
  '/client-plan-wizard',
  '/client-control',
  '/care-plan',
  '/questionnaire',
  '/anaya-intake',
  '/intake-form',
  '/parent-concerns',
  '/games',
  '/session-',
  '/milestone-',
  '/add-milestone',
  '/daily-diary',
  '/focus-areas',
  '/assessment',
  '/gap-analysis',
  '/iep-analysis-results',
  '/goal-coach',
  '/therapist-dashboard',
  '/therapist-choose-plan',
  '/payment-success',
  '/delete-my-',
  '/provider-verification',
  '/pending-verification',
  '/role-selection',
  '/team-list',
  '/team-management',
  '/team-request',
  '/refer-parent',
  '/feedback',
  '/blog/create',
  '/demo',
  '/intern-pending',
  '/parent-goal-chat',
];

export const normalizePath = (path: string): string => {
  if (!path) return '/';
  const withoutTrailingSlash = path.length > 1 ? path.replace(/\/+$/, '') : path;
  return withoutTrailingSlash || '/';
};

export const getSEOForPath = (path: string): SEOConfig => {
  const normalizedPath = normalizePath(path);

  if (normalizedPath.startsWith('/social-stories/gate')) {
    return {
      ...defaultSEO,
      title: 'Social Stories | TinyWins',
      description: 'Sign in to access TinyWins social stories.',
      robots: 'noindex,nofollow',
      url: `${BASE_URL}${normalizedPath}`,
    };
  }

  if (normalizedPath.startsWith('/social-stories/') && normalizedPath !== '/social-stories') {
    const slug = normalizedPath
      .replace(/^\/social-stories\/(view|gate)\//, '')
      .replace(/^\/social-stories\//, '')
      .split('/')[0] || '';
    const titleFromSlug = slug
      .replace(/-with-song$/i, '')
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    const nice = titleFromSlug
      ? `${titleFromSlug} | Social Story | TinyWins`
      : 'Social Story | TinyWins';
    const canonicalPath = slug ? `/social-stories/view/${slug}` : '/social-stories';
    return {
      ...defaultSEO,
      title: nice,
      description: titleFromSlug
        ? `Read the “${titleFromSlug}” social story on TinyWins — educational support for everyday routines.`
        : 'Educational social stories for everyday routines on TinyWins.',
      keywords: ['social stories', 'TinyWins social story', titleFromSlug || 'life skills story'],
      type: 'article',
      robots: 'index,follow',
      url: `${BASE_URL}${canonicalPath}`,
    };
  }

  if (normalizedPath.startsWith('/parents/blog/')) {
    return {
      ...defaultSEO,
      title: 'Parent Guide | TinyWins',
      description: 'Practical parent guides on routines, communication, and developmental support.',
      keywords: ['autism parent guide', 'child development tips'],
      type: 'article',
      robots: 'index,follow',
      url: `${BASE_URL}${normalizedPath}`,
    };
  }

  if (normalizedPath.startsWith('/blog/') && normalizedPath !== '/blog') {
    return {
      ...defaultSEO,
      title: 'Article | TinyWins Blog',
      description:
        'Insights on neurodivergent child development, therapy collaboration, and family-first strategies.',
      keywords: ['TinyWins blog article', 'neurodivergent child development'],
      type: 'article',
      robots: 'index,follow',
      url: `${BASE_URL}${normalizedPath}`,
    };
  }

  const matchingPrivatePath = privatePathPrefixes.find(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`),
  );
  if (matchingPrivatePath) {
    return {
      ...defaultSEO,
      title: 'TinyWins App',
      description: 'Secure TinyWins application area.',
      keywords: ['TinyWins app'],
      robots: 'noindex,nofollow',
      url: `${BASE_URL}${normalizedPath}`,
    };
  }

  const configured = publicPageSEO[normalizedPath] ?? defaultSEO;
  return {
    ...defaultSEO,
    ...configured,
    url: configured.url ?? `${BASE_URL}${normalizedPath === '/' ? '' : normalizedPath}`,
    image: configured.image ?? DEFAULT_OG_IMAGE,
  };
};

export const INDEXABLE_PUBLIC_PATHS: string[] = [
  '/',
  '/parents',
  '/therapists',
  '/schools-and-counties',
  '/how-it-works',
  '/resources',
  '/about',
  '/contact',
  '/privacy-policy',
  '/terms',
  '/developmental-milestone-tracker',
  '/iep-goal-coach',
  '/social-stories',
  '/sensory-strategies',
  '/developmental-goals/communication',
  '/developmental-goals/social-emotional',
  '/developmental-goals/daily-living',
  '/developmental-goals/cognitive',
  '/developmental-goals/motor-skills',
  '/resources/iep',
  '/resources/communication',
  '/resources/sensory',
  '/resources/behavior-and-regulation',
  '/resources/visual-schedules',
  '/team',
  '/support',
  '/therapist-marketplace',
  '/book-meeting',
  '/internships',
];

export const keywordMap: Record<string, { primary: string; secondary: string[] }> = {
  '/': {
    primary: 'developmental support for neurodivergent children',
    secondary: ['developmental milestone tracker', 'sensory-aware strategies'],
  },
};
